import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import { classesApi } from '@/api';
import type { ILearnClassesRaw, IThunkAPI } from '@/store/types';
import type { IClassesState } from '@/api/types';
import { KnownErrors } from '@/store/enums';

export const initialState: IClassesState = {
  classesList: null,
  isClassesError: false,
  isClassesSuccess: false,
  isClassesFetching: false,
  selectedClassId: 0,
  selectedClassName: '',
};

const transformClassList = (classes: ILearnClassesRaw[]) => {
  return classes.map((cls: ILearnClassesRaw) => {
    return {
      ...cls,
      isOpen: false,
    };
  });
};

export const getClassesStatus = createAsyncThunk<ILearnClassesRaw[], number | null | undefined, IThunkAPI>(
  'get/classes/clientId',
  async (clientID, thunkAPI) => {
    try {
      const response = await classesApi.getClassesStatus(clientID);
      let data =
        (response.data && response.data.length && Array.isArray(response.data) && transformClassList(response.data)) ||
        [];
      if (response.data === KnownErrors.ItemNotFound) {
        data = [];
      }
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const classesSlice = createSlice({
  name: 'classes',
  initialState,
  reducers: {
    clearclassess(state) {
      state.classesList = [];
    },
    toggleClassCard(state, { payload }) {
      const { classId } = payload;
      if (classId && Array.isArray(state.classesList) && state.classesList.length) {
        for (let i = 0; i < state.classesList.length; i++) {
          if (state.classesList[i].classId === classId) {
            state.classesList[i].isOpen = !state.classesList[i].isOpen || false;
            break;
          }
        }
      }
    },
    updateSelectedClass(state, { payload }) {
      state.selectedClassId = payload?.selectedClassId;
      if (!payload.selectedClassName && Array.isArray(state.classesList) && state.classesList.length) {
        let className = '';
        for (let i = 0; i < state.classesList.length; i++) {
          if (state.classesList[i].classId === payload?.selectedClassId) {
            className = state.classesList[i].className;
            break;
          }
        }
        state.selectedClassName = className;
      } else {
        state.selectedClassName = payload?.selectedClassName || '';
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getClassesStatus.fulfilled, (state, action) => {
        if (action.payload && Array.isArray(action.payload) && action.payload?.length > 0) {
          state.classesList = action.payload;
        } else {
          state.classesList = [];
        }
        state.isClassesSuccess = true;
        state.isClassesError = false;
        state.isClassesFetching = false;
      })
      .addCase(getClassesStatus.rejected, state => {
        state.isClassesError = true;
        state.isClassesSuccess = false;
        state.isClassesFetching = false;
      })
      .addCase(getClassesStatus.pending, state => {
        state.isClassesFetching = true;
      });
  },
});

// actions
export const { clearclassess, updateSelectedClass, toggleClassCard } = classesSlice.actions;

// selectors
export const classes = (state: RootState): IClassesState['classesList'] => state.classes.classesList;
export const isClassesFetching = (state: RootState): boolean => state.classes.isClassesFetching;
export const selectedClassId = (state: RootState): number => state.classes.selectedClassId;
export const selectedClassName = (state: RootState): string => state.classes.selectedClassName;

export default classesSlice.reducer;
